import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import {
  useActiveLanguageIsUnseeded,
  useLanguageCode,
} from "../lib/activeLanguage";
import { categoryText, cefrText, masteryBar } from "../lib/palette";
import { requireAuth } from "../lib/requireAuth";
import { UnseededLanguageLanding } from "../lib/UnseededLanguageLanding";
import { FilterSelect, type FilterOption } from "../components/FilterSelect";
import { FilterBar, FilterEmpty } from "../components/FilterBar";
import { PageEyebrow } from "../components/PageEyebrow";
import { Centered } from "../components/Centered";

type Mastery = "all" | "weak" | "due" | "strong";

const MASTERY_OPTIONS: FilterOption[] = [
  { value: "all", label: "All skills" },
  { value: "weak", label: "Weak (P < 40%)" },
  { value: "due", label: "Due review (P < 70%)" },
  { value: "strong", label: "Strong (P ≥ 80%)" },
];

export const Route = createFileRoute("/skills")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: SkillsPage,
});

function SkillsPage() {
  const language = useLanguageCode();
  const unseeded = useActiveLanguageIsUnseeded();
  const [category, setCategory] = useState<string>("");
  const [cefr, setCefr] = useState<string>("");
  const [mastery, setMastery] = useState<Mastery>("all");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["skills-mastery", language],
    queryFn: async () => {
      const { data, error } = await api.GET("/skills/mastery", {
        params: { query: { language } },
      });
      if (error) throw error;
      return data;
    },
    // Skills mastery changes on every attempt (via React Query
    // invalidation in the practice page) but doesn't shift mid-page
    // navigation. 30s is a fair window before background-refetching
    // on remount.
    staleTime: 30_000,
    enabled: !unseeded,
  });

  const categoryOptions = useMemo<FilterOption[]>(() => {
    const set = new Set<string>();
    for (const s of data ?? []) set.add(s.category);
    return [...set]
      .sort()
      .map((c) => ({ value: c, label: humanizeCategory(c) }));
  }, [data]);

  const cefrOptions = useMemo<FilterOption[]>(() => {
    const set = new Set<string>();
    for (const s of data ?? []) if (s.cefrLevel) set.add(s.cefrLevel);
    return [...set].sort().map((c) => ({ value: c, label: c }));
  }, [data]);

  if (unseeded) return <UnseededLanguageLanding />;
  if (isLoading) {
    return <Centered>Loading skills…</Centered>;
  }
  if (isError || !data) {
    return <Centered>Could not load skills.</Centered>;
  }

  if (data.length === 0) {
    return (
      <Centered>
        <p className="text-ink-700 mb-2">No skills seeded yet.</p>
        <p className="text-ink-400 text-sm">
          Run <code className="bg-ink-100 px-1 rounded">make seed</code> to load
          German A1.
        </p>
      </Centered>
    );
  }

  const masteryOk = (p: number, n: number) => {
    if (mastery === "all") return true;
    if (n === 0) return mastery === "weak"; // untouched = weakest
    if (mastery === "weak") return p < 0.4;
    if (mastery === "due") return p < 0.7;
    if (mastery === "strong") return p >= 0.8;
    return true;
  };

  // Group by category for a cleaner layout — filters are applied
  // before grouping so the per-category counts reflect what's visible.
  const filtered = data.filter(
    (s) =>
      (!category || s.category === category) &&
      (!cefr || s.cefrLevel === cefr) &&
      masteryOk(s.retrievability, s.nAttempts),
  );
  const byCategory = new Map<string, typeof data>();
  for (const s of filtered) {
    const bucket = byCategory.get(s.category) ?? [];
    bucket.push(s);
    byCategory.set(s.category, bucket);
  }
  const shownCount = filtered.length;

  // Total + per-category counts so the section header carries its
  // weight in the visual rhythm — Yuki (2026-05-23) noted the
  // category split is meaningful in the data model but flattened in
  // the UI; surfacing the raw code keeps distinct categories
  // distinguishable even when humanizeCategory collapses two
  // adjacent slugs onto the same English label.
  const total = data.length;

  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow label="Reference" mono={`${language}.skills`} />
      <h2 className="font-serif text-3xl text-ink-900 mb-2 tracking-tight">
        Skills
      </h2>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-xl">
        Every grammar concept the practice queue can test, with the model's
        current memory probability and how often you've attempted it.
      </p>
      <div className="hairline mb-6 sm:mb-8" />

      <FilterBar
        shown={shownCount}
        total={total}
        onClear={
          category || cefr || mastery !== "all"
            ? () => {
                setCategory("");
                setCefr("");
                setMastery("all");
              }
            : undefined
        }
      >
        <FilterSelect
          layout="inline"
          label="Category"
          value={category}
          onChange={setCategory}
          options={categoryOptions}
          placeholder="All categories"
        />
        <FilterSelect
          layout="inline"
          label="CEFR"
          value={cefr}
          onChange={setCefr}
          options={cefrOptions}
          placeholder="All levels"
        />
        <FilterSelect
          layout="inline"
          label="Mastery"
          value={mastery}
          onChange={(v) => setMastery((v as Mastery) || "all")}
          options={MASTERY_OPTIONS}
          required
        />
      </FilterBar>

      {shownCount === 0 && <FilterEmpty noun="skills" />}

      {[...byCategory.entries()].map(([category, skills]) => (
        <div key={category} className="mb-8 sm:mb-10">
          <div className="mb-3 flex items-baseline gap-3">
            <h3 className="font-serif text-lg text-ink-900">
              {humanizeCategory(category)}
            </h3>
            <span
              className={`font-mono text-[11px] font-medium tabular-nums ${categoryText(category)}`}
              title={`Internal category code · ${skills.length} of ${total} skills`}
            >
              {category} · {skills.length}
            </span>
          </div>
          <ul className="divide-y divide-ink-100 border border-ink-100 bg-paper">
            {skills.map((s) => (
              <li
                key={s.id}
                className="px-3 sm:px-4 py-3 hover:bg-ink-50 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/progress/skill/$skillCode"
                      params={{ skillCode: s.code }}
                      className="text-ink-900 text-sm sm:text-base hover:underline underline-offset-2 decoration-1"
                      title={`${s.code} — view progress over time`}
                    >
                      {s.name}
                    </Link>
                  </div>
                  <div className="text-xs text-ink-400 flex items-center gap-3 shrink-0">
                    <span
                      className={`font-mono font-semibold ${s.cefrLevel ? cefrText(s.cefrLevel) : "text-ink-400"}`}
                    >
                      {s.cefrLevel ?? "—"}
                    </span>
                    {/* Per-skill attempt counts. Labeled `tried` because
                        Yuki (2026-05-23 feedback) parsed the unlabeled
                        `26/47` next to a `100%` recall figure as if they
                        were two views of the same metric; explicit label
                        breaks the implied equivalence. */}
                    <span
                      className="tabular-nums"
                      title={`Tried ${s.nAttempts} times; got ${s.nCorrect} correct. This is raw attempt history, not the model's belief.`}
                    >
                      tried {s.nCorrect}/{s.nAttempts}
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0 bg-ink-100 h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        s.nAttempts > 0 ? masteryBar(s.retrievability) : ""
                      }`}
                      style={{
                        width:
                          s.nAttempts > 0
                            ? `${Math.round(s.retrievability * 100)}%`
                            : "0%",
                      }}
                    />
                  </div>
                  {/* Memory probability (P) — labeled inline so the % can't
                      be mistaken for raw accuracy (see `tried` above). For
                      untouched skills the model has no evidence — render
                      a dash, not `0%`, so a learner doesn't read it as
                      "the app thinks I'll fail this". */}
                  <span
                    className="text-xs text-ink-700 tabular-nums shrink-0"
                    title="Memory probability: the model's current chance you'd get this skill right now, before any format adjustment"
                  >
                    {s.nAttempts > 0
                      ? `P ${(s.retrievability * 100).toFixed(0)}%`
                      : "P —"}
                  </span>
                  <span
                    className="text-[10px] text-ink-400 tabular-nums shrink-0"
                    title="Stability: days until P drops to 90%"
                  >
                    S{" "}
                    {s.stability < 1
                      ? `${Math.round(s.stability * 24)}h`
                      : `${s.stability.toFixed(1)}d`}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

// Internal skill categories are short slugs ("gram", "vocab", "phono",
// "morpho"); render them with proper English nouns so the page reads
// like documentation, not like a CSV column.
function humanizeCategory(category: string): string {
  const map: Record<string, string> = {
    gram: "Grammar",
    vocab: "Vocabulary",
    phono: "Pronunciation",
    morpho: "Morphology",
    pragma: "Pragmatics",
    lex: "Lexicon",
  };
  return map[category] ?? category.charAt(0).toUpperCase() + category.slice(1);
}
